import { useEffect, useState } from "react";
import { X, Cookie } from "lucide-react";
import { api } from "@/lib/api";

const CONSENT_KEY = "auraglow_consent";

let loaded = false;
const googleAds = { id: null, label: null };

function injectGA4(id) {
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", id);
}

function injectGoogleAds(id) {
  if (!window.gtag) {
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
  }
  window.gtag("config", id);
}

function injectGTM(id) {
  window.dataLayer = window.dataLayer || [];
  /* eslint-disable */
  (function (w, d, s, l, i) {
    w[l] = w[l] || []; w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    var f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l != "dataLayer" ? "&l=" + l : "";
    j.async = true; j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", id);
  /* eslint-enable */
}

function injectMeta(id) {
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", id);
  window.fbq("track", "PageView");
}

function injectTikTok(id) {
  /* eslint-disable */
  !(function (w, d, t) {
    w.TiktokAnalyticsObject = t; var ttq = (w[t] = w[t] || []);
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
    ttq.setAndDefer = function (e, n) { e[n] = function () { e.push([n].concat(Array.prototype.slice.call(arguments, 0))); }; };
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.load = function (e) {
      var n = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = n; ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
      var o = d.createElement("script"); o.type = "text/javascript"; o.async = !0; o.src = n + "?sdkid=" + e + "&lib=" + t;
      var a = d.getElementsByTagName("script")[0]; a.parentNode.insertBefore(o, a);
    };
    ttq.load(id); ttq.page();
  })(window, document, "ttq");
  /* eslint-enable */
}

function injectRaw(html, target) {
  if (!html) return;
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const node = tpl.content.cloneNode(true);
  // re-create <script> tags so they execute
  node.querySelectorAll("script").forEach((old) => {
    const s = document.createElement("script");
    [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
    s.textContent = old.textContent;
    old.replaceWith(s);
  });
  (target === "body" ? document.body : document.head).appendChild(node);
}

let ready = false;
const queue = [];

// Loads pixels on page load (no consent gate). IDs come from admin Settings (/api/site-config), env fallback.
async function loadTrackers() {
  if (loaded || typeof window === "undefined") return;
  loaded = true;
  let t = {};
  try {
    const { data } = await api.get("/site-config");
    t = data?.tracking || {};
  } catch {}
  const ga4 = t.ga4_id || process.env.REACT_APP_GA4_ID;
  const meta = t.meta_pixel_id || process.env.REACT_APP_META_PIXEL_ID;
  const tiktok = t.tiktok_id || process.env.REACT_APP_TIKTOK_PIXEL_ID;
  const gtm = t.gtm_id;
  const adsId = t.google_ads_id || process.env.REACT_APP_GOOGLE_ADS_ID;
  const adsLabel = t.google_ads_purchase_label || process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL;
  if (ga4) injectGA4(ga4);
  if (gtm) injectGTM(gtm);
  if (meta) injectMeta(meta);
  if (tiktok) injectTikTok(tiktok);
  if (adsId) { injectGoogleAds(adsId); googleAds.id = adsId; googleAds.label = adsLabel; }
  if (t.custom_head) injectRaw(t.custom_head, "head");
  if (t.custom_body) injectRaw(t.custom_body, "body");
  ready = true;
  // Flush any events that fired before the pixels finished loading.
  while (queue.length) {
    const { event, params, eventId } = queue.shift();
    dispatch(event, params, eventId);
  }
}

// Standard Meta event name -> GA4 recommended ecommerce event name.
const GA4_MAP = {
  ViewContent: "view_item",
  AddToCart: "add_to_cart",
  InitiateCheckout: "begin_checkout",
  AddPaymentInfo: "add_payment_info",
  Purchase: "purchase",
  Lead: "generate_lead",
};

function dispatch(event, params, eventId) {
  try {
    if (window.fbq) window.fbq("track", event, params, { eventID: eventId });
    if (window.gtag) window.gtag("event", GA4_MAP[event] || event, params);
    if (window.ttq) window.ttq.track(event, params, { event_id: eventId });
    // Google Ads Purchase conversion (dynamic value, transaction_id dedupes across reloads).
    if (event === "Purchase" && window.gtag && googleAds.id && googleAds.label) {
      window.gtag("event", "conversion", {
        send_to: `${googleAds.id}/${googleAds.label}`,
        value: params.value,
        currency: params.currency || "AUD",
        transaction_id: eventId,
      });
    }
  } catch (_) {}
  // Server-side relay for match quality. Purchase is sent authoritatively by the backend.
  if (event !== "Purchase") relayCapi(event, params, eventId);
}

function cookie(name) {
  const m = document.cookie.match("(?:^|; )" + name + "=([^;]*)");
  return m ? decodeURIComponent(m[1]) : undefined;
}

function relayCapi(event, params, eventId) {
  try {
    api.post("/meta/capi/event", {
      event_name: event,
      event_id: eventId,
      event_source_url: window.location.href,
      fbp: cookie("_fbp"),
      fbc: cookie("_fbc"),
      value: params.value,
      currency: params.currency || "AUD",
      content_ids: params.content_ids,
      content_type: params.content_type,
      num_items: params.num_items,
    }).catch(() => {});
  } catch (_) {}
}

// Fire a conversion event across whichever pixels are active. Queues until pixels are ready.
// Pass eventId to keep browser + server-side events deduplicated in Meta.
export function track(event, params = {}, eventId) {
  if (typeof window === "undefined") return;
  const id = eventId || (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  if (!ready) { queue.push({ event, params, eventId: id }); return; }
  dispatch(event, params, id);
}

export const Analytics = () => {
  useEffect(() => {
    loadTrackers();
  }, []);
  return null;
};
